import React, { useState, useRef, useEffect } from 'react';
import { 
  Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, 
  Container, Divider, FormControl, Grid, InputLabel, MenuItem, 
  Paper, Select, Snackbar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Zoom, IconButton,
  useTheme, Slide, Tooltip, Collapse
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import KitchenIcon from '@mui/icons-material/Kitchen';
import TimerIcon from '@mui/icons-material/Timer';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MessageIcon from '@mui/icons-material/Message';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import { generateRecipe } from '../services/openRouterService';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSupabaseAuth } from '../hooks/useSupabaseAuth';
import SupabaseService from '../supabase/supabaseService';

const RecipeGenerator = () => {
  const { user } = useSupabaseAuth();
  const theme = useTheme();
  const resultRef = useRef(null);
  const [ingredients, setIngredients] = useState('');
  const [dietary, setDietary] = useState('');
  const [mealType, setMealType] = useState('dinner');
  const [cuisine, setCuisine] = useState('');
  const [cookingTime, setCookingTime] = useState('any');
  const [complexity, setComplexity] = useState('medium');
  
  const [loading, setLoading] = useState(false);
  const [generatingStep, setGeneratingStep] = useState(0);
  const [recipe, setRecipe] = useState(null);
  const [parsedRecipe, setParsedRecipe] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [isFavorite, setIsFavorite] = useState(false);
  const [savedRecipeId, setSavedRecipeId] = useState(null);
  const [showTips, setShowTips] = useState(false);

  // Psychology-informed color palette
  const colors = {
    primary: '#43a047', // Green - represents health and freshness
    secondary: '#ff9800', // Orange - stimulates appetite
    accent: '#8561c5', // Purple - inspires creativity
    background: '#f9fbf6', // Very light green - calming, clean
    success: '#4caf50', // Green - positive reinforcement
    warning: '#ff9800', // Orange - attention
    light: '#f5f7fa', // Light grey-blue - subtle background
    dark: '#37474f' // Dark blue-grey - text
  };

  // AI generation steps to simulate conversational interaction
  const generationSteps = [
    "Analyzing ingredients...",
    "Exploring recipe ideas...",
    "Calculating nutritional balance...",
    "Finalizing cooking instructions...",
    "Preparing your recipe..."
  ];

  // Effect to advance generation steps during loading
  useEffect(() => {
    let interval;
    if (loading) {
      interval = setInterval(() => {
        setGeneratingStep(prev => (prev < generationSteps.length - 1 ? prev + 1 : prev));
      }, 2000);
    } else {
      setGeneratingStep(0);
    }
    
    return () => clearInterval(interval);
  }, [loading]);

  // Parse recipe when recipe text changes
  useEffect(() => {
    if (recipe) {
      parseRecipeText(recipe);
    }
  }, [recipe]);

  const parseRecipeText = (recipeText) => {
    try {
      // First sanitize the text to remove markdown formatting
      const sanitizedText = sanitizeRecipeText(recipeText);
      
      // Initialize with default structure
      const parsed = {
        title: 'Recipe',
        preparationTime: 'N/A',
        cookingTime: 'N/A',
        servings: '1-2',
        ingredients: [],
        instructions: [],
        nutritionalInfo: [],
        tips: []
      };

      // Extract title (usually the first line)
      const titleMatch = sanitizedText.match(/^([\s\S]*?)(?:\n|$)/);
      if (titleMatch && titleMatch[1]) {
        parsed.title = titleMatch[1].trim().replace(/^\d+\.\s*/, '');
      }

      // Extract preparation time
      const prepTimeMatch = sanitizedText.match(/preparation time:?\s*([^\n]+)/i);
      if (prepTimeMatch) parsed.preparationTime = prepTimeMatch[1].trim();

      // Extract cooking time
      const cookTimeMatch = sanitizedText.match(/cooking time:?\s*([^\n]+)/i);
      if (cookTimeMatch) parsed.cookingTime = cookTimeMatch[1].trim();

      // Extract servings
      const servingsMatch = sanitizedText.match(/servings:?\s*([^\n]+)/i);
      if (servingsMatch) parsed.servings = servingsMatch[1].trim();

      // Extract ingredients
      const ingredientSection = sanitizedText.match(/ingredients:?\s*([\s\S]*?)(?:instructions:|directions:|method:|steps:|$)/i);
      if (ingredientSection && ingredientSection[1]) {
        parsed.ingredients = ingredientSection[1].trim().split('\n')
          .map(item => item.trim())
          .map(item => cleanListItem(item)) // Clean each ingredient line
          .filter(item => item && !item.toLowerCase().includes('ingredient'));
      }

      // Extract instructions
      const instructionSection = sanitizedText.match(/(?:instructions|directions|method|steps):?\s*([\s\S]*?)(?:nutritional|tips|notes|$)/i);
      if (instructionSection && instructionSection[1]) {
        parsed.instructions = instructionSection[1].trim().split(/\d+\.\s*/)
          .map(item => item.trim())
          .map(item => cleanListItem(item)) // Clean each instruction
          .filter(item => item && !item.toLowerCase().includes('instruction') && !item.toLowerCase().includes('step'));
      }

      // Extract nutritional information
      const nutritionSection = sanitizedText.match(/nutritional(?:\s*information)?:?\s*([\s\S]*?)(?:tips|notes|$)/i);
      if (nutritionSection && nutritionSection[1]) {
        parsed.nutritionalInfo = nutritionSection[1].trim().split('\n')
          .map(item => item.trim())
          .map(item => cleanListItem(item)) // Clean each nutrition item
          .filter(item => item);
      }

      // Extract tips
      const tipsSection = sanitizedText.match(/(?:tips|notes):?\s*([\s\S]*?)$/i);
      if (tipsSection && tipsSection[1]) {
        parsed.tips = tipsSection[1].trim().split('\n')
          .map(item => item.trim().replace(/^-\s*/, ''))
          .map(item => cleanListItem(item)) // Clean each tip
          .filter(item => item);
      }

      setParsedRecipe(parsed);
    } catch (error) {
      console.error('Error parsing recipe:', error);
      // If parsing fails, just set a basic structure
      setParsedRecipe({
        title: 'Recipe',
        ingredients: [],
        instructions: [],
        nutritionalInfo: [],
        tips: []
      });
    }
  };

  /**
   * Sanitize recipe text to remove markdown formatting and special characters
   */
  const sanitizeRecipeText = (text) => {
    if (!text) return '';
    
    // Remove markdown headings (# Heading)
    let sanitized = text.replace(/^#+\s+(.*)$/gm, '$1');
    
    // Remove bold/italic markdown (*text* or **text**)
    sanitized = sanitized.replace(/(\*\*|\*)(.*?)(\*\*|\*)/g, '$2');
    
    // Replace bullet points
    sanitized = sanitized.replace(/^\s*[\*\-•]\s+(.*)$/gm, '$1');
    
    return sanitized;
  };

  /**
   * Clean individual list items from asterisks and hashes
   */
  const cleanListItem = (item) => {
    if (!item) return '';
    
    // Remove leading asterisks and dashes that might be used as bullet points
    let cleaned = item.replace(/^[\*\-•]\s*/, '');
    
    // Remove any bold/italic formatting
    cleaned = cleaned.replace(/(\*\*|\*)(.*?)(\*\*|\*)/g, '$2');
    
    // Remove any markdown heading notation
    cleaned = cleaned.replace(/^#+\s+/, '');
    
    return cleaned;
  };

  const handleGenerateRecipe = async () => {
    if (!ingredients.trim()) {
      toast.warning('Please enter some ingredients to get started!');
      return;
    }

    try {
      setLoading(true);
      setRecipe(null);
      setParsedRecipe(null);
      setIsFavorite(false);
      setSavedRecipeId(null);
      
      // Generate recipe with enhanced parameters
      const generatedRecipe = await generateRecipe({
        ingredients,
        dietary,
        mealType,
        cuisine,
        cookingTime,
        complexity,
        nutritionalGoals: { balanced: true }
      });
      
      setRecipe(generatedRecipe);
      
      // Success notification
      toast.success('Recipe created successfully!', {
        icon: <AutoAwesomeIcon style={{ color: colors.secondary }} />
      });
      
    } catch (error) {
      console.error('Error generating recipe:', error);
      toast.error('Failed to generate recipe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.info('Please log in to save recipes to your favorites');
      return;
    }

    try {
      setLoading(true);
      
      if (isFavorite && savedRecipeId) {
        // Remove from favorites
        await SupabaseService.toggleRecipeFavorite(savedRecipeId, false);
        setIsFavorite(false);
        toast.success('Recipe removed from favorites');
      } else {
        let recipeId;
        
        if (savedRecipeId) {
          // Update existing recipe
          await SupabaseService.toggleRecipeFavorite(savedRecipeId, true);
          recipeId = savedRecipeId;
        } else {
          // Add new recipe
          const newRecipe = await SupabaseService.addRecord('recipes', {
            ingredients,
            dietary,
            meal_type: mealType,
            cuisine,
            cooking_time: cookingTime,
            complexity,
            recipe_text: recipe,
            parsed_recipe: parsedRecipe,
            is_favorite: true
          });
          
          recipeId = newRecipe.id;
          setSavedRecipeId(recipeId);
        }
        
        setIsFavorite(true);
        toast.success('Recipe saved to favorites');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // SupabaseService already handles the toast notification
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!recipe || !parsedRecipe) return;
    
    try {
      toast.info('Preparing your PDF...');
      
      if (resultRef.current) {
        const canvas = await html2canvas(resultRef.current, {
          scale: 2,
          useCORS: true,
          logging: false
        });
        
        const imgData = canvas.toDataURL('image/png');
        
        // Create a new PDF document
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });
        
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 15;
        
        // Add header with styling
        doc.setFillColor(colors.primary);
        doc.rect(0, 0, pageWidth, 30, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.text('Meal Maestro', margin, 15);
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'italic');
        doc.text('Your AI-Generated Recipe', pageWidth - margin, 15, { align: 'right' });
        
        // Add title
        doc.setTextColor(33, 33, 33);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(parsedRecipe.title, pageWidth / 2, 45, { align: 'center' });
        
        // Add recipe image
        const imgWidth = pageWidth - (margin * 2);
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        doc.addImage(imgData, 'PNG', margin, 55, imgWidth, imgHeight);
        
        // Add recipe details
        let yPos = 55 + imgHeight + 15;
        
        // Check if we need a new page
        if (yPos > pageHeight - 50) {
          doc.addPage();
          yPos = margin;
        }
        
        // Recipe details section
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, yPos, pageWidth - (margin * 2), 25, 'F');
        
        doc.setFontSize(12);
        doc.setTextColor(33, 33, 33);
        
        // Details table
        yPos += 8;
        doc.setFont('helvetica', 'normal');
        doc.text(`Preparation: ${parsedRecipe.preparationTime}`, margin + 5, yPos);
        doc.text(`Cooking: ${parsedRecipe.cookingTime}`, margin + 70, yPos);
        doc.text(`Servings: ${parsedRecipe.servings}`, margin + 135, yPos);
        
        // Ingredients section
        yPos += 25;
        if (yPos > pageHeight - 50) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.primary);
        doc.text('Ingredients', margin, yPos);
        
        doc.setDrawColor(colors.primary);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos + 2, margin + 40, yPos + 2);
        
        yPos += 10;
        doc.setFontSize(11);
        doc.setTextColor(33, 33, 33);
        doc.setFont('helvetica', 'normal');
        
        parsedRecipe.ingredients.forEach(ingredient => {
          if (yPos > pageHeight - 15) {
            doc.addPage();
            yPos = margin;
          }
          
          doc.text(`• ${ingredient}`, margin, yPos);
          yPos += 7;
        });
        
        // Instructions section
        yPos += 10;
        if (yPos > pageHeight - 50) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(colors.primary);
        doc.text('Instructions', margin, yPos);
        
        doc.setDrawColor(colors.primary);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos + 2, margin + 40, yPos + 2);
        
        yPos += 10;
        doc.setFontSize(11);
        doc.setTextColor(33, 33, 33);
        doc.setFont('helvetica', 'normal');
        
        parsedRecipe.instructions.forEach((instruction, index) => {
          if (!instruction) return;
          
          if (yPos > pageHeight - 20) {
            doc.addPage();
            yPos = margin;
          }
          
          doc.setFont('helvetica', 'bold');
          doc.text(`${index + 1}.`, margin, yPos);
          doc.setFont('helvetica', 'normal');
          
          const textLines = doc.splitTextToSize(instruction, pageWidth - margin * 2 - 10);
          doc.text(textLines, margin + 10, yPos);
          
          yPos += (textLines.length * 7) + 5;
        });
        
        // Nutritional information
        if (parsedRecipe.nutritionalInfo.length > 0) {
          yPos += 5;
          if (yPos > pageHeight - 50) {
            doc.addPage();
            yPos = margin;
          }
          
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(colors.primary);
          doc.text('Nutritional Information', margin, yPos);
          
          doc.setDrawColor(colors.primary);
          doc.setLineWidth(0.5);
          doc.line(margin, yPos + 2, margin + 60, yPos + 2);
          
          yPos += 10;
          doc.setFontSize(11);
          doc.setTextColor(33, 33, 33);
          doc.setFont('helvetica', 'normal');
          
          parsedRecipe.nutritionalInfo.forEach(info => {
            if (yPos > pageHeight - 15) {
              doc.addPage();
              yPos = margin;
            }
            
            doc.text(`• ${info}`, margin, yPos);
            yPos += 7;
          });
        }
        
        // Tips section if available
        if (parsedRecipe.tips && parsedRecipe.tips.length > 0) {
          yPos += 5;
          if (yPos > pageHeight - 50) {
            doc.addPage();
            yPos = margin;
          }
          
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(colors.primary);
          doc.text('Tips for Success', margin, yPos);
          
          doc.setDrawColor(colors.primary);
          doc.setLineWidth(0.5);
          doc.line(margin, yPos + 2, margin + 45, yPos + 2);
          
          yPos += 10;
          doc.setFontSize(11);
          doc.setTextColor(33, 33, 33);
          doc.setFont('helvetica', 'normal');
          
          parsedRecipe.tips.forEach(tip => {
            if (yPos > pageHeight - 15) {
              doc.addPage();
              yPos = margin;
            }
            
            doc.text(`• ${tip}`, margin, yPos);
            yPos += 7;
          });
        }
        
        // Footer
        doc.setFontSize(9);
        doc.setTextColor(120, 120, 120);
        doc.text('Generated by Meal Maestro - AI-Powered Recipe Generator', pageWidth / 2, pageHeight - 10, { align: 'center' });
        
        // Save the PDF
        doc.save(`${parsedRecipe.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
        toast.success('Recipe PDF downloaded successfully!');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  const closeNotification = () => {
    setNotification({...notification, open: false});
  };

  // Sample popular combinations
  const popularCombinations = [
    { name: 'Italian Pasta', ingredients: 'pasta, tomatoes, garlic, olive oil, basil' },
    { name: 'Taco Night', ingredients: 'ground beef, lettuce, tomatoes, cheese, tortillas, avocado' },
    { name: 'Stir Fry', ingredients: 'chicken, bell peppers, broccoli, carrots, soy sauce, ginger' },
    { name: 'Mediterranean', ingredients: 'chickpeas, cucumber, feta cheese, olives, red onion, olive oil' }
  ];
  
  const cookingTimeOptions = [
    { value: 'quick', label: 'Quick (under 30 min)' },
    { value: 'medium', label: 'Medium (30-60 min)' },
    { value: 'long', label: 'Long (over 60 min)' },
    { value: 'any', label: 'Any time' },
  ];
  
  const complexityOptions = [
    { value: 'simple', label: 'Simple - Beginner friendly' },
    { value: 'medium', label: 'Medium - Some cooking experience' },
    { value: 'advanced', label: 'Advanced - Experienced cooks' },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, sm: 4 }, 
          borderRadius: 2,
          background: `linear-gradient(to bottom right, ${colors.background}, white)`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Decorative elements */}
        <Box 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            right: 0, 
            width: 200, 
            height: 200, 
            background: `linear-gradient(to bottom left, ${colors.primary}20, transparent)`,
            borderRadius: '0 0 0 100%',
            zIndex: 0
          }} 
        />
        
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
            <Grid item>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: `${colors.primary}15`,
                  color: colors.primary
                }}
              >
                <RestaurantIcon fontSize="large" />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography variant="h4" sx={{ 
                color: colors.primary, 
                fontWeight: 600,
                mb: 0.5
              }}>
                Recipe Generator
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Create delicious recipes from your available ingredients with AI assistance
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={5} lg={4}>
              <Card 
                elevation={0} 
                variant="outlined" 
                sx={{ 
                  height: '100%', 
                  borderRadius: 2,
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: colors.primary,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ 
                    color: colors.dark, 
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <KitchenIcon fontSize="small" /> What's in your kitchen?
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      label="Ingredients Available"
                      placeholder="e.g., chicken, rice, broccoli, olive oil"
                      value={ingredients}
                      onChange={(e) => setIngredients(e.target.value)}
                      multiline
                      rows={4}
                      variant="outlined"
                      sx={{ 
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: colors.primary
                          },
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Enter ingredients separated by commas
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom sx={{ color: colors.dark }}>
                      Popular Combinations
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {popularCombinations.map((combo) => (
                        <Chip 
                          key={combo.name} 
                          label={combo.name} 
                          size="small"
                          onClick={() => setIngredients(combo.ingredients)}
                          sx={{ 
                            borderColor: `${colors.primary}60`,
                            bgcolor: 'transparent',
                            '&:hover': { 
                              bgcolor: `${colors.primary}15`,
                              borderColor: colors.primary
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ color: colors.dark, mb: 1 }}>
                      Recipe Preferences
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id="meal-type-label">Meal Type</InputLabel>
                          <Select
                            labelId="meal-type-label"
                            value={mealType}
                            onChange={(e) => setMealType(e.target.value)}
                            label="Meal Type"
                          >
                            <MenuItem value="breakfast">Breakfast</MenuItem>
                            <MenuItem value="lunch">Lunch</MenuItem>
                            <MenuItem value="dinner">Dinner</MenuItem>
                            <MenuItem value="snack">Snack</MenuItem>
                            <MenuItem value="dessert">Dessert</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id="cooking-time-label">Cooking Time</InputLabel>
                          <Select
                            labelId="cooking-time-label"
                            value={cookingTime}
                            onChange={(e) => setCookingTime(e.target.value)}
                            label="Cooking Time"
                          >
                            {cookingTimeOptions.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Dietary Restrictions"
                          placeholder="e.g., vegetarian, gluten-free"
                          value={dietary}
                          onChange={(e) => setDietary(e.target.value)}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id="complexity-label">Complexity</InputLabel>
                          <Select
                            labelId="complexity-label"
                            value={complexity}
                            onChange={(e) => setComplexity(e.target.value)}
                            label="Complexity"
                          >
                            {complexityOptions.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Cuisine (Optional)"
                          placeholder="e.g., Italian, Mexican"
                          value={cuisine}
                          onChange={(e) => setCuisine(e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />
                  
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleGenerateRecipe}
                    disabled={loading || !ingredients.trim()}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                    sx={{
                      py: 1.5,
                      bgcolor: colors.primary,
                      '&:hover': {
                        bgcolor: '#388e3c'
                      },
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(67, 160, 71, 0.2)'
                    }}
                  >
                    {loading ? 'Creating Recipe...' : 'Generate Recipe'}
                  </Button>
                  
                  {!loading && !recipe && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <Tooltip
                        title={showTips ? "Hide cooking tips" : "Show cooking tips"}
                        placement="top"
                      >
                        <IconButton 
                          size="small" 
                          onClick={() => setShowTips(!showTips)}
                          sx={{ color: colors.secondary }}
                        >
                          <TipsAndUpdatesIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {showTips ? "Hide tips" : "Cooking tips"}
                      </Typography>
                    </Box>
                  )}
                  
                  <Collapse in={showTips}>
                    <Box sx={{ mt: 2, bgcolor: `${colors.secondary}10`, p: 2, borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ color: colors.secondary }}>
                        Cooking Tips
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • Add acid (lemon, vinegar) to brighten flavors
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        • Salt throughout cooking, not just at the end
                      </Typography>
                      <Typography variant="body2" color="text.secondary"> 
                        • Let meat rest before slicing to retain juices
                      </Typography>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={7} lg={8}>
              <Card 
                variant="outlined" 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  borderRadius: 2,
                  borderColor: recipe ? colors.primary : 'rgba(0, 0, 0, 0.12)',
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }}
                ref={resultRef}
              >
                <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                  {loading ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      py: 8
                    }}>
                      <CircularProgress sx={{ mb: 3, color: colors.primary }} />
                      
                      <Typography variant="h6" align="center" sx={{ mb: 1 }}>
                        {generationSteps[generatingStep]}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {Array(5).fill(0).map((_, i) => (
                          <Box 
                            key={i} 
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              mx: 0.5,
                              bgcolor: i <= generatingStep ? colors.primary : '#e0e0e0',
                              transition: 'all 0.5s ease'
                            }} 
                          />
                        ))}
                      </Box>
                      
                      <Box sx={{ 
                        maxWidth: 400, 
                        margin: '0 auto', 
                        p: 2, 
                        bgcolor: `${colors.primary}10`,
                        borderRadius: 2,
                        mt: 2,
                        display: 'flex'
                      }}>
                        <MessageIcon sx={{ mr: 1, color: colors.primary, fontSize: 20, mt: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          {generatingStep === 0 && "I'm checking what recipes are possible with your ingredients..."}
                          {generatingStep === 1 && "Looking at different cuisine options that match your preferences..."}
                          {generatingStep === 2 && "Working on a nutritionally balanced recipe for you..."}
                          {generatingStep === 3 && "Creating clear, easy-to-follow cooking instructions..."}
                          {generatingStep === 4 && "Almost there! Putting the final touches on your recipe..."}
                        </Typography>
                      </Box>
                    </Box>
                  ) : parsedRecipe ? (
                    <Box sx={{ height: '100%' }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        mb: 2
                      }}>
                        <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                          <Typography variant="h5" sx={{ 
                            color: colors.dark, 
                            fontWeight: 600,
                            lineHeight: 1.3
                          }}>
                            {parsedRecipe.title}
                          </Typography>
                        </Zoom>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={isFavorite ? "Remove from favorites" : "Save to favorites"}>
                            <IconButton 
                              onClick={toggleFavorite}
                              size="small"
                              sx={{ 
                                color: isFavorite ? '#f44336' : 'text.secondary',
                                '&:hover': {
                                  bgcolor: isFavorite ? 'rgba(244, 67, 54, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            >
                              {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Download Recipe PDF">
                            <IconButton 
                              onClick={downloadPDF}
                              size="small"
                              sx={{ 
                                color: 'text.secondary',
                                '&:hover': {
                                  bgcolor: 'rgba(0, 0, 0, 0.04)'
                                }
                              }}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      
                      <Slide direction="up" in={true} mountOnEnter unmountOnExit>
                        <Box sx={{ mb: 3 }}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={4}>
                              <Box sx={{ 
                                p: 1.5, 
                                bgcolor: `${colors.primary}10`, 
                                borderRadius: 1,
                                textAlign: 'center'
                              }}>
                                <TimerIcon sx={{ color: colors.primary, mb: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                  Preparation
                                </Typography>
                                <Typography variant="subtitle2">
                                  {parsedRecipe.preparationTime}
                                </Typography>
                              </Box>
                            </Grid>
                            
                            <Grid item xs={12} sm={4}>
                              <Box sx={{ 
                                p: 1.5, 
                                bgcolor: `${colors.accent}10`, 
                                borderRadius: 1,
                                textAlign: 'center'
                              }}>
                                <FastfoodIcon sx={{ color: colors.accent, mb: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                  Cooking
                                </Typography>
                                <Typography variant="subtitle2">
                                  {parsedRecipe.cookingTime}
                                </Typography>
                              </Box>
                            </Grid>
                            
                            <Grid item xs={12} sm={4}>
                              <Box sx={{ 
                                p: 1.5, 
                                bgcolor: `${colors.secondary}10`, 
                                borderRadius: 1,
                                textAlign: 'center'
                              }}>
                                <RestaurantIcon sx={{ color: colors.secondary, mb: 0.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                  Servings
                                </Typography>
                                <Typography variant="subtitle2">
                                  {parsedRecipe.servings}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Slide>
                      
                      <Box sx={{ 
                        maxHeight: 500, 
                        overflowY: 'auto', 
                        pr: 1,
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: '#f1f1f1',
                          borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#c1c1c1',
                          borderRadius: '10px',
                        }
                      }}>
                        {/* Ingredients */}
                        <Slide direction="right" in={true} mountOnEnter unmountOnExit style={{ transitionDelay: '200ms' }}>
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ 
                              mb: 2, 
                              color: colors.primary, 
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <KitchenIcon fontSize="small" /> Ingredients
                            </Typography>
                            
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                              <Table size="small">
                                <TableBody>
                                  {parsedRecipe.ingredients.map((ingredient, index) => (
                                    <TableRow 
                                      key={index}
                                      sx={{ 
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.02)' }
                                      }}
                                    >
                                      <TableCell sx={{ py: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <CheckCircleIcon sx={{ 
                                            fontSize: 18, 
                                            color: colors.primary,
                                            opacity: 0.6,
                                            mr: 1.5
                                          }} />
                                          {ingredient}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        </Slide>
                        
                        {/* Instructions */}
                        <Slide direction="left" in={true} mountOnEnter unmountOnExit style={{ transitionDelay: '300ms' }}>
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ 
                              mb: 2, 
                              color: colors.accent, 
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <LocalDiningIcon fontSize="small" /> Instructions
                            </Typography>
                            
                            <Box>
                              {parsedRecipe.instructions.map((instruction, index) => {
                                if (!instruction) return null;
                                return (
                                  <Box 
                                    key={index} 
                                    sx={{ 
                                      mb: 3,
                                      display: 'flex'
                                    }}
                                  >
                                    <Avatar 
                                      sx={{ 
                                        width: 36, 
                                        height: 36, 
                                        bgcolor: `${colors.accent}15`,
                                        color: colors.accent,
                                        fontWeight: 'bold',
                                        mr: 2
                                      }}
                                    >
                                      {index + 1}
                                    </Avatar>
                                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                                      {instruction}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        </Slide>
                        
                        {/* Nutritional Information */}
                        {parsedRecipe.nutritionalInfo.length > 0 && (
                          <Slide direction="right" in={true} mountOnEnter unmountOnExit style={{ transitionDelay: '400ms' }}>
                            <Box sx={{ mb: 4 }}>
                              <Typography variant="h6" sx={{ 
                                mb: 2, 
                                color: colors.secondary, 
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <RestaurantIcon fontSize="small" /> Nutritional Information
                              </Typography>
                              
                              <Paper 
                                variant="outlined" 
                                sx={{ 
                                  p: 2, 
                                  borderRadius: 2,
                                  bgcolor: `${colors.secondary}05`
                                }}
                              >
                                <Grid container spacing={1}>
                                  {parsedRecipe.nutritionalInfo.map((info, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box 
                                          component="span" 
                                          sx={{ 
                                            display: 'inline-block', 
                                            width: 8, 
                                            height: 8, 
                                            borderRadius: '50%', 
                                            bgcolor: colors.secondary,
                                            mr: 1
                                          }} 
                                        />
                                        {info}
                                      </Typography>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Paper>
                            </Box>
                          </Slide>
                        )}
                        
                        {/* Cooking Tips */}
                        {parsedRecipe.tips && parsedRecipe.tips.length > 0 && (
                          <Slide direction="left" in={true} mountOnEnter unmountOnExit style={{ transitionDelay: '500ms' }}>
                            <Box>
                              <Typography variant="h6" sx={{ 
                                mb: 2, 
                                color: colors.primary, 
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <TipsAndUpdatesIcon fontSize="small" /> Tips for Success
                              </Typography>
                              
                              <Box sx={{ 
                                p: 2, 
                                bgcolor: `${colors.primary}08`, 
                                borderRadius: 2,
                                border: `1px dashed ${colors.primary}40`
                              }}>
                                {parsedRecipe.tips.map((tip, index) => (
                                  <Typography 
                                    key={index} 
                                    variant="body2" 
                                    sx={{ 
                                      mb: index < parsedRecipe.tips.length - 1 ? 1 : 0,
                                      display: 'flex',
                                      alignItems: 'flex-start'
                                    }}
                                  >
                                    <TipsAndUpdatesIcon 
                                      sx={{ 
                                        color: colors.primary, 
                                        fontSize: 18, 
                                        mr: 1,
                                        mt: 0.3
                                      }} 
                                    />
                                    {tip}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          </Slide>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      py: 8
                    }}>
                      <FastfoodIcon sx={{ fontSize: 80, color: `${colors.primary}30`, mb: 3 }} />
                      <Typography variant="h6" align="center" gutterBottom>
                        Your Recipe Will Appear Here
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
                        Enter your available ingredients and preferences, then click "Generate Recipe" 
                        to create a personalized recipe just for you.
                      </Typography>
                      
                      <Box sx={{ 
                        mt: 4, 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <VisibilityIcon sx={{ color: colors.secondary }} />
                        <Typography variant="body2" color="text.secondary">
                          Try being specific with ingredients for better results
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      
      <Snackbar open={notification.open} autoHideDuration={6000} onClose={closeNotification}>
        <Alert onClose={closeNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RecipeGenerator;
